import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { objectsApi } from "@/services/api-v2.service";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { GripVertical, Info, Pencil, RotateCcw, X } from "lucide-react";
import type { GeometryCreate, GeometryUpdate, SystemStats } from "@/types/api-v2";
import {
  geometriesListQueryKey,
  publishCommittedGeometry,
} from "@/components/map-libre/committed-geometry";
import { convertCircleToPolygon } from "@/lib/utils";
import { alignGeoJsonToPrimitiveCode } from "@/lib/geojson-primitive-align";
import { messageFromThrownApiError } from "@/lib/geometry-api-errors";
import { computeDrawingDirty } from "@/lib/drawing-dirty";
import { useDrawingSessionKeyboard } from "@/hooks/useDrawingSessionKeyboard";
import { useCoarsePointer } from "@/hooks/useCoarsePointer";
import { normalizePrimitiveCode } from "@/lib/map-geometry-tools";
import { hasSelectionPolygon } from "@/lib/osm-selection-polygon";
import { MapPanelChrome } from "@/components/map-libre/MapPanelChrome";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { MAP_PANEL_Z } from "@/lib/map-panel-z";
import { cn } from "@/lib/utils";

/** Отступ панели от краёв области карты (совпадает с left-4 / bottom-4). */
const PANEL_EDGE = 16;

type PanelPoint = { x: number; y: number };

function clampPanelPosition(
  x: number,
  y: number,
  panelW: number,
  panelH: number,
  parentW: number,
  parentH: number,
): PanelPoint {
  const maxX = Math.max(PANEL_EDGE, parentW - panelW - PANEL_EDGE);
  const maxY = Math.max(PANEL_EDGE, parentH - panelH - PANEL_EDGE);
  return {
    x: Math.min(maxX, Math.max(PANEL_EDGE, x)),
    y: Math.min(maxY, Math.max(PANEL_EDGE, y)),
  };
}

function isDragIgnoredTarget(target: EventTarget | null): boolean {
  return target instanceof Element
    && Boolean(target.closest("button, a, input, textarea, select, [data-no-drag]"));
}

/** Область карты: offsetParent, иначе ближайший предок с нестатичной позицией. */
function mapCanvasOf(el: HTMLElement): HTMLElement | null {
  const offset = el.offsetParent;
  if (offset instanceof HTMLElement) return offset;
  let node = el.parentElement;
  while (node) {
    const position = node.style.position;
    if (position && position !== "static") return node;
    node = node.parentElement;
  }
  return null;
}

interface DrawingPanelProps {
  activeTool: string | null;
  drawing: any;
  onSave: () => void;
  onCancel: () => void;
  objectId?: number | null;
  /** Уведомление родителя о наличии несохранённых изменений (горячие клавиши, баннер) */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Режим «сначала карта»: объект ещё не выбран — по «Сохранить» вызывается анализ пересечений, не POST геометрии.
   */
  mapFirstMode?: boolean;
  onMapFirstCommit?: () => void | Promise<void>;
  /**
   * Синхронизация полей панели с родителем (имя/описание/цвет), чтобы при «Создать объект»
   * из модалки анализа передать актуальные значения в initial_geometry.
   */
  mapFirstSnapshotRef?: MutableRefObject<{
    name: string;
    description: string;
    color: string;
    opacity: number;
  }>;
  /** Импорт зданий из OSM по текущему полигону выделения */
  onCreateFromOsm?: () => void;
}

function getPanelTitle(
  isEditing: boolean,
  activeTool: string | null,
  drawingName?: string,
  primitiveCode?: string,
): { title: string; subtitle?: string } {
  const prim = normalizePrimitiveCode(primitiveCode || "");
  if (isEditing) {
    return {
      title: "Редактирование контура",
      subtitle: drawingName?.trim() || undefined,
    };
  }
  if (!activeTool) return { title: "Рисование" };
  if (prim === "multipolygon" && activeTool === "draw-polygon") {
    return {
      title: "Несколько полигонов",
      subtitle: "MultiPolygon — несколько контуров за одну сессию",
    };
  }
  if (prim === "multilinestring" && activeTool === "draw-line") {
    return { title: "Несколько линий", subtitle: "MultiLineString" };
  }
  if (prim === "multipoint" && activeTool === "draw-point") {
    return { title: "Несколько точек", subtitle: "MultiPoint" };
  }
  switch (activeTool) {
    case "draw-point":
      return { title: "Рисование точки" };
    case "draw-line":
      return { title: "Рисование линии" };
    case "draw-polygon":
      return { title: "Рисование полигона" };
    case "draw-rectangle":
      return { title: "Рисование прямоугольника" };
    case "draw-circle":
      return { title: "Рисование круга" };
    case "draw-freehand":
      if (prim === "polygon" || prim === "multipolygon") {
        return { title: "Рисование контура от руки", subtitle: "Контур плавно повторяет движение курсора" };
      }
      return { title: "Рисование линии от руки" };
    case "draw-angled-rectangle":
      return { title: "Прямоугольник под углом" };
    case "draw-sensor":
      return { title: "Сектор датчика (угол обзора)" };
    case "draw-sector":
      return { title: "Рисование сектора" };
    default:
      return { title: "Рисование" };
  }
}

function getHintLines(
  isEditing: boolean,
  activeTool: string | null,
  primitiveCode?: string,
): string[] {
  const prim = normalizePrimitiveCode(primitiveCode || "");
  if (isEditing) {
    return [
      "Измените узлы на карте, затем нажмите «Сохранить».",
      "Восстановить исходный контур — кнопка сброса у заголовка панели.",
    ];
  }
  if (prim === "multipoint" && activeTool === "draw-point") {
    return [
      "Каждый клик добавляет вершину MultiPoint.",
      "Enter на карте (вне полей ввода) — завершить. Backspace — убрать последнюю точку.",
    ];
  }
  if (prim === "multilinestring" && activeTool === "draw-line") {
    return [
      "Клики задают вершины текущей линии; двойной клик завершает линию и начинает следующую.",
      "Enter — сохранить все линии. Backspace — удалить последнюю вершину или последнюю завершённую линию.",
    ];
  }
  if (prim === "multipolygon" && activeTool === "draw-polygon") {
    return [
      "Каждый контур: ≥3 вершины, замыкание — клик у первой точки или двойной клик.",
      "После замыкания начинается следующий контур. Enter — сохранить все контуры. Backspace — отмена шага.",
    ];
  }
  switch (activeTool) {
    case "draw-point":
      return ["Один клик по карте — точка."];
    case "draw-line":
      return [
        "Клики задают вершины линии.",
        "Двойной клик завершает линию.",
      ];
    case "draw-polygon":
      return [
        "Клики задают вершины полигона.",
        "Клик близко к первой точке или двойной клик замыкают контур.",
      ];
    case "draw-rectangle":
      return ["Первый клик — угол, второй — противоположный угол прямоугольника."];
    case "draw-circle":
      return [
        "Первый клик — центр, второй — точка на окружности (радиус).",
      ];
    case "draw-freehand":
      if (prim === "polygon" || prim === "multipolygon") {
        return [
          "Первый клик — начало контура; ведите мышь — контур плавно следует за курсором.",
          "Завершить: второй клик или обведите стартовую точку (замыкание). Enter — завершить.",
        ];
      }
      return [
        "Первый клик — начало линии; рисуйте, ведя мышь по карте.",
        "Завершить: второй клик или Enter на карте. Escape — отменить.",
      ];
    case "draw-angled-rectangle":
      return [
        "Первый клик — угол, второй задаёт направление и длину.",
        "Третий клик задаёт ширину и завершает прямоугольник.",
      ];
    case "draw-sensor":
      return [
        "Первый клик — центр датчика, второй — начало дуги обзора.",
        "Третий клик задаёт угол; четвёртый завершает сектор.",
      ];
    case "draw-sector":
      return [
        "Первый клик — вершина сектора, второй — точка на дуге.",
        "Третий клик завершает сектор.",
      ];
    default:
      return [];
  }
}

export default function DrawingPanel({
  activeTool,
  drawing,
  onSave,
  onCancel,
  objectId,
  onDirtyChange,
  mapFirstMode = false,
  onMapFirstCommit,
  mapFirstSnapshotRef,
  onCreateFromOsm,
}: DrawingPanelProps) {
  const [name, setName] = useState(drawing?.isEditing && drawing?.geometryId ? drawing?.name || "" : "");
  const [description, setDescription] = useState(
    drawing?.isEditing && drawing?.geometryId ? drawing?.description || "" : "",
  );
  const [color, setColor] = useState("#3B82F6");
  const [opacity, setOpacity] = useState(50);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const coarsePointer = useCoarsePointer();
  const rootRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  /** null — якорь слева снизу; после перетаскивания — left/top в координатах карты. */
  const [pos, setPos] = useState<PanelPoint | null>(null);
  /** height = null — по содержимому; число — панель ужата, тело скроллится. */
  const [slot, setSlot] = useState<{ maxHeight: number; height: number | null } | null>(null);

  const isEditing = Boolean(drawing?.isEditing && drawing?.geometryId);
  const primitiveCode =
    typeof drawing?.geometry_primitive_code === "string" ? drawing.geometry_primitive_code : undefined;
  const { title: panelTitle, subtitle: panelSubtitle } = getPanelTitle(
    isEditing,
    activeTool,
    drawing?.name,
    primitiveCode,
  );
  const hintLines = getHintLines(isEditing, activeTool, primitiveCode);

  useLayoutEffect(() => {
    const el = rootRef.current;
    const parent = el ? mapCanvasOf(el) : null;
    if (!el || !parent) return;

    const fit = () => {
      const chrome = el.firstElementChild instanceof HTMLElement ? el.firstElementChild : null;
      const available = Math.max(160, parent.clientHeight - PANEL_EDGE * 2);
      const saved = {
        maxHeight: el.style.maxHeight,
        height: el.style.height,
        chromeHeight: chrome?.style.height ?? "",
        chromeFlex: chrome?.style.flex ?? "",
      };
      el.style.maxHeight = "none";
      el.style.height = "auto";
      if (chrome) {
        chrome.style.height = "auto";
        chrome.style.flex = "none";
      }
      const needed = el.offsetHeight;
      el.style.maxHeight = saved.maxHeight;
      el.style.height = saved.height;
      if (chrome) {
        chrome.style.height = saved.chromeHeight;
        chrome.style.flex = saved.chromeFlex;
      }
      const lock = needed > available + 1;
      setSlot((prev) => {
        const next = { maxHeight: available, height: lock ? available : null };
        if (prev && prev.maxHeight === next.maxHeight && prev.height === next.height) return prev;
        return next;
      });
    };

    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [isEditing, activeTool, primitiveCode, coarsePointer, hintLines.length]);

  useEffect(() => {
    const onResize = () => {
      const el = rootRef.current;
      const parent = el ? mapCanvasOf(el) : null;
      if (!el || !parent || pos == null) return;
      setPos((current) => {
        if (!current) return current;
        return clampPanelPosition(
          current.x,
          current.y,
          el.offsetWidth,
          el.offsetHeight,
          parent.clientWidth,
          parent.clientHeight,
        );
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos]);

  const onHeaderPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || isDragIgnoredTarget(e.target)) return;
    const root = rootRef.current;
    const parent = root ? mapCanvasOf(root) : null;
    if (!root || !parent) return;
    e.preventDefault();
    const origin = clampPanelPosition(
      root.offsetLeft,
      root.offsetTop,
      root.offsetWidth,
      root.offsetHeight,
      parent.clientWidth,
      parent.clientHeight,
    );
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: origin.x,
      origY: origin.y,
    };
    setPos(origin);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* указатель уже захвачен или среда без capture */
    }
  };

  const onHeaderPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const root = rootRef.current;
    const parent = root ? mapCanvasOf(root) : null;
    if (!root || !parent) return;
    setPos(
      clampPanelPosition(
        drag.origX + (e.clientX - drag.startX),
        drag.origY + (e.clientY - drag.startY),
        root.offsetWidth,
        root.offsetHeight,
        parent.clientWidth,
        parent.clientHeight,
      ),
    );
  };

  const onHeaderPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* захват уже снят */
    }
  };

  const originalName = drawing?.originalName ?? drawing?.name ?? "";
  const originalDescription = drawing?.originalDescription ?? drawing?.description ?? "";
  const originalStyle = drawing?.originalStyle ?? drawing?.style ?? {};
  const originalColor =
    typeof originalStyle?.color === "string"
      ? originalStyle.color
      : typeof originalStyle?.fillColor === "string"
        ? originalStyle.fillColor
        : "#3B82F6";
  const originalOpacity =
    typeof originalStyle?.opacity === "number"
      ? Math.round(Math.min(1, Math.max(0, originalStyle.opacity)) * 100)
      : 50;

  useEffect(() => {
    if (isEditing) {
      setName(drawing?.name || "");
      setDescription(drawing?.description || "");
      const styleColor = drawing?.style?.color || drawing?.style?.fillColor || "#3B82F6";
      setColor(styleColor);
      const styleOpacity = typeof drawing?.style?.opacity === "number"
        ? Math.round(drawing.style.opacity * 100)
        : 50;
      setOpacity(Math.min(100, Math.max(0, styleOpacity)));
      return;
    }
    setName("");
    setDescription("");
    setColor("#3B82F6");
    setOpacity(50);
  }, [drawing, isEditing]);

  const isDirty = useMemo(
    () =>
      computeDrawingDirty({
        isEditing,
        currentGeo: drawing?.geoJSON,
        originalGeo: drawing?.originalGeoJSON,
        name,
        description,
        color,
        opacity,
        originalName,
        originalDescription,
        originalColor,
        originalOpacity,
      }),
    [
      isEditing,
      drawing?.geoJSON,
      drawing?.originalGeoJSON,
      name,
      description,
      color,
      opacity,
      originalName,
      originalDescription,
      originalColor,
      originalOpacity,
    ],
  );

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  useEffect(() => {
    if (!mapFirstSnapshotRef) return;
    mapFirstSnapshotRef.current = { name, description, color, opacity };
  }, [name, description, color, opacity, mapFirstSnapshotRef]);

  const geometryTypeLabel = typeof drawing?.type === "string" ? drawing.type : "";
  const isMultiComplex =
    isEditing &&
    geometryTypeLabel &&
    /multi/i.test(geometryTypeLabel) &&
    !/^marker$/i.test(geometryTypeLabel);

  const isCircleEdit = isEditing && String(drawing?.type || "").toLowerCase() === "circle";

  const requestExit = useCallback(() => {
    if (isDirty) {
      setShowDiscardDialog(true);
      return;
    }
    onCancel();
  }, [isDirty, onCancel]);

  useDrawingSessionKeyboard(true, requestExit);

  const handleResetEditing = () => {
    if (!isEditing || !drawing) return;
    setName(drawing?.name || "");
    setDescription(drawing?.description || "");
    const styleColor = drawing?.style?.color || drawing?.style?.fillColor || "#3B82F6";
    setColor(styleColor);
    const styleOpacity = typeof drawing?.style?.opacity === "number"
      ? Math.round(drawing.style.opacity * 100)
      : 50;
    setOpacity(Math.min(100, Math.max(0, styleOpacity)));

    window.dispatchEvent(
      new CustomEvent("restoreEditingGeometry", {
        detail: {
          geometry: {
            id: drawing.geometryId,
            object_id: drawing.objectId,
            geojson: drawing.originalGeoJSON || drawing.geoJSON,
            type: drawing.type,
            name: drawing.originalName || drawing.name || "",
            description: drawing.originalDescription || drawing.description || "",
            style: drawing.originalStyle || drawing.style || {},
            geometry_kls_id: drawing.geometry_kls_id,
            geometry_primitive_kls_id: drawing.geometry_primitive_kls_id,
          },
          objectId: drawing.objectId,
        },
      }),
    );
  };

  const handleSave = async () => {
    if (!name) {
      toast({
        title: "Введите название",
        description: "Название геометрии обязательно",
        variant: "destructive",
      });
      return;
    }

    try {
      let geometryType;
      if (isEditing && drawing.type) {
        geometryType = drawing.type;
      } else if (drawing && "type" in drawing && drawing.type) {
        geometryType = drawing.type;
      } else if (activeTool && activeTool.includes("draw-")) {
        geometryType = activeTool.replace("draw-", "");
      } else {
        try {
          const geoJSON = typeof drawing.geoJSON === "string"
            ? JSON.parse(drawing.geoJSON)
            : drawing.geoJSON || drawing;

          if (geoJSON && geoJSON.geometry && geoJSON.geometry.type) {
            geometryType = geoJSON.geometry.type.toLowerCase();
          } else {
            geometryType = "polygon";
          }
        } catch (error) {
          console.error("Ошибка при определении типа геометрии:", error);
          geometryType = "polygon";
        }
      }

      if (!geometryType) {
        toast({
          title: "Ошибка определения типа геометрии",
          description: "Не удалось определить тип создаваемой геометрии. Попробуйте заново.",
          variant: "destructive",
        });
        return;
      }

      if (!drawing) {
        toast({
          title: "Ошибка",
          description: "Данные геометрии отсутствуют",
          variant: "destructive",
        });
        return;
      }

      if (!isEditing) {
        const sem = drawing.geometry_kls_id;
        if (sem == null || Number.isNaN(Number(sem))) {
          toast({
            title: "Выберите тип геометрии",
            description: "Укажите семантический тип (KLS) в диалоге перед сохранением новой геометрии.",
            variant: "destructive",
          });
          return;
        }
      }

      const targetObjectId =
        objectId ?? drawing?.objectId ?? drawing?.object_id ?? null;

      const mapFirst =
        Boolean(mapFirstMode && onMapFirstCommit && !isEditing && !targetObjectId);

      if (mapFirst) {
        await onMapFirstCommit!();
        return;
      }

      if (!targetObjectId) {
        toast({
          title: "Геометрия должна быть привязана к объекту",
          description:
            "Пожалуйста, сначала выберите объект на карте или создайте новый объект, а затем добавьте к нему геометрию.",
          variant: "destructive",
        });
        return;
      }

      let geoJSONData = typeof drawing.geoJSON === "string"
        ? JSON.parse(drawing.geoJSON)
        : (drawing.geoJSON || drawing);

      if (geometryType === "circle" || drawing.type === "circle") {
        geoJSONData = convertCircleToPolygon(geoJSONData);
      }

      const innerTypeBefore = (() => {
        try {
          const o = geoJSONData as Record<string, unknown>;
          const g =
            o?.type === "Feature" && o.geometry && typeof o.geometry === "object"
              ? (o.geometry as Record<string, unknown>)
              : o;
          return String(g?.type || "").toLowerCase();
        } catch {
          return "";
        }
      })();

      const aligned = alignGeoJsonToPrimitiveCode(
        geoJSONData as GeoJSON.Feature,
        typeof drawing?.geometry_primitive_code === "string"
          ? drawing.geometry_primitive_code
          : undefined,
      );
      geoJSONData = aligned.geojson;
      if (innerTypeBefore && aligned.geometryTypeLower !== innerTypeBefore) {
        geometryType = aligned.geometryTypeLower;
      }

      if (isEditing) {
        const geometryId = drawing.geometryId;
        try {
          const updateData: GeometryUpdate = {
            name,
            description,
            type: geometryType,
            geojson: geoJSONData,
            style: {
              color,
              fillColor: color,
              weight: 2,
              opacity: opacity / 100,
              fillOpacity: opacity / 200,
            },
          };
          const kSem = drawing?.geometry_kls_id;
          const kPri = drawing?.geometry_primitive_kls_id;
          if (kSem != null && Number.isFinite(Number(kSem)) && Number(kSem) > 0) {
            updateData.geometry_kls_id = Number(kSem);
          }
          if (kPri != null && Number.isFinite(Number(kPri)) && Number(kPri) > 0) {
            updateData.geometry_primitive_kls_id = Number(kPri);
          }

          const objectKey = Number(targetObjectId);
          const geometriesQueryKey = geometriesListQueryKey(objectKey);
          await queryClient.cancelQueries({ queryKey: geometriesQueryKey });
          queryClient.setQueryData(geometriesQueryKey, (old: unknown) => {
            const list = Array.isArray(old) ? old : [];
            return list.map((g: Record<string, unknown> & { id: number }) =>
              Number(g.id) === Number(geometryId)
                ? {
                    ...g,
                    name,
                    description,
                    type: geometryType,
                    geojson: geoJSONData,
                    geometry_kls_id: drawing?.geometry_kls_id,
                    geometry_primitive_kls_id: drawing?.geometry_primitive_kls_id,
                    style: updateData.style,
                  }
                : g
            );
          });

          const response = await objectsApi.updateGeometry(objectKey, geometryId, updateData);

          await queryClient.cancelQueries({ queryKey: geometriesQueryKey });
          queryClient.setQueryData(geometriesQueryKey, (old: unknown) => {
            const list = Array.isArray(old) ? old : [];
            return list.map((g: { id: number }) =>
              Number(g.id) === Number(geometryId) ? response : g
            );
          });
          publishCommittedGeometry({ objectId: objectKey, geometry: response });
          await queryClient.invalidateQueries({ queryKey: geometriesQueryKey });
          await queryClient.refetchQueries({ queryKey: geometriesQueryKey });
          queryClient.invalidateQueries({ queryKey: ["geometries"] });

          queryClient.invalidateQueries({ queryKey: ["stats"] });
          queryClient.refetchQueries({ queryKey: ["stats"] });

          toast({
            title: "Успешно обновлено",
            description: "Геометрия успешно изменена",
          });
        } catch (error) {
          console.error("Ошибка при обновлении геометрии:", error);
          toast({
            title: "Ошибка обновления",
            description: messageFromThrownApiError(error, "Не удалось обновить геометрию"),
            variant: "destructive",
          });
          throw error;
        }
      } else {
        try {
          const geometryData: GeometryCreate = {
            object_id: targetObjectId,
            name,
            description,
            type: geometryType,
            geojson: geoJSONData,
            geometry_kls_id: drawing?.geometry_kls_id,
            geometry_primitive_kls_id: drawing?.geometry_primitive_kls_id,
            style: {
              color,
              fillColor: color,
              weight: 2,
              opacity: opacity / 100,
              fillOpacity: opacity / 200,
            },
          };

          const objectKey = Number(targetObjectId);
          const geometriesQueryKey = geometriesListQueryKey(objectKey);

          const tempId = Date.now();
          await queryClient.cancelQueries({ queryKey: geometriesQueryKey });
          queryClient.setQueryData(geometriesQueryKey, (old: any) => {
            const oldGeometries = Array.isArray(old) ? old : [];
            return [...oldGeometries, { ...geometryData, id: tempId, created_at: new Date().toISOString() }];
          });

          const response = await objectsApi.addGeometry(objectKey, {
            ...geometryData,
            object_id: objectKey,
          });

          await queryClient.cancelQueries({ queryKey: geometriesQueryKey });
          queryClient.setQueryData(geometriesQueryKey, (old: any) => {
            const oldGeometries = Array.isArray(old) ? old : [];
            return oldGeometries.filter((g: any) => g.id !== tempId).concat([response]);
          });
          publishCommittedGeometry({ objectId: objectKey, geometry: response });

          queryClient.setQueryData<SystemStats>(["stats"], (old) => {
            if (!old) return old;
            return {
              ...old,
              totalGeometries: (old.totalGeometries || 0) + 1,
            };
          });

          await queryClient.invalidateQueries({ queryKey: geometriesQueryKey });
          await queryClient.refetchQueries({ queryKey: geometriesQueryKey });

          await queryClient.invalidateQueries({ queryKey: ["geometries"] });

          await queryClient.invalidateQueries({ queryKey: ["stats"] });
          await queryClient.refetchQueries({ queryKey: ["stats"] });

          toast({
            title: "Успешно сохранено",
            description: "Геометрия успешно добавлена",
          });
        } catch (error) {
          console.error("Ошибка при создании геометрии:", error);
          toast({
            title: "Ошибка сохранения",
            description: messageFromThrownApiError(error, "Не удалось создать геометрию"),
            variant: "destructive",
          });
          throw error;
        }
      }

      queryClient.invalidateQueries({ queryKey: ["objects"] });

      onSave();
    } catch (error) {
      console.error("Ошибка сохранения геометрии:", error);
      toast({
        title: "Ошибка сохранения",
        description: messageFromThrownApiError(error, "Не удалось сохранить геометрию"),
        variant: "destructive",
      });
    }
  };

  return (
    <>
      <div
        ref={rootRef}
        className={cn(
          "absolute flex w-[min(20rem,calc(100%-2rem))] min-h-0 max-h-[calc(100%-2rem)] flex-col overflow-hidden",
          MAP_PANEL_Z.mapPanel,
        )}
        style={{
          position: "absolute",
          maxHeight: slot?.maxHeight,
          height: slot?.height ?? "auto",
          ...(pos
            ? { left: pos.x, top: pos.y }
            : { left: PANEL_EDGE, bottom: PANEL_EDGE }),
        }}
      >
        <MapPanelChrome
          className="max-h-full min-h-0"
          style={
            slot?.height != null
              ? { height: "100%", flex: "1 1 auto", minHeight: 0 }
              : undefined
          }
          title={panelTitle}
          subtitle={panelSubtitle}
          titleLeading={
            <span className="mt-0.5 flex items-center gap-1 text-muted-foreground" title="Перетащите за шапку">
              <GripVertical className="h-4 w-4 shrink-0" aria-hidden />
              {isEditing ? <Pencil className="h-4 w-4 shrink-0" aria-hidden /> : null}
            </span>
          }
          headerClassName="cursor-grab touch-none select-none active:cursor-grabbing"
          onHeaderPointerDown={onHeaderPointerDown}
          onHeaderPointerMove={onHeaderPointerMove}
          onHeaderPointerUp={onHeaderPointerUp}
          onHeaderPointerCancel={onHeaderPointerUp}
          headerActions={
            <>
              <SectionHelpTooltip label="Справка: панель геометрии">
                <p>Название, описание и стиль контура. Сохранение записывает геометрию на объект.</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>Измените узлы на карте, затем нажмите «Сохранить».</li>
                  <li>Панель можно перетащить за шапку, если она закрывает контур.</li>
                  <li>Сброс у заголовка возвращает исходный контур в режиме редактирования.</li>
                  <li>Escape — выйти из режима. Если есть правки, появится подтверждение.</li>
                </ul>
              </SectionHelpTooltip>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleResetEditing}
                disabled={!isEditing}
                title={
                  isEditing
                    ? "Восстановить исходное состояние редактирования"
                    : "Доступно только в режиме редактирования"
                }
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                onClick={requestExit}
                title="Закрыть режим рисования"
                type="button"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          }
          footer={
            <>
              {onCreateFromOsm && !isEditing && hasSelectionPolygon(drawing) && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onCreateFromOsm}
                  title="Создать объекты из зданий OSM в выделенном полигоне"
                >
                  Создать из OSM
                </Button>
              )}
              <Button variant="outline" onClick={requestExit} type="button">
                Отмена
              </Button>
              <Button
                onClick={handleSave}
                type="button"
                variant={isDirty ? "default" : "secondary"}
                className={isDirty ? "" : "text-muted-foreground"}
              >
                {isDirty ? "Сохранить изменения" : "Сохранить"}
              </Button>
            </>
          }
        >
          {hintLines.length > 0 && (
            <div className="space-y-1 border-b border-border bg-muted/40 px-3 py-2">
              {hintLines.map((line) => (
                <p key={line} className="text-[11px] leading-snug text-muted-foreground">
                  {line}
                </p>
              ))}
            </div>
          )}
          {isEditing && (
            <div className="border-b border-border px-3 py-2">
              <Alert className="py-2">
                <Info className="h-4 w-4" />
                <AlertTitle className="text-xs font-medium leading-tight">Шаги</AlertTitle>
                <AlertDescription className="text-[11px] leading-snug">
                  1. Измените контур на карте. 2. При необходимости скорректируйте название и стиль. 3. Нажмите
                  «Сохранить».
                </AlertDescription>
              </Alert>
            </div>
          )}
          {isEditing && coarsePointer && (
            <div className="border-b border-border px-3 py-2">
              <Alert className="border-dashed py-2">
                <AlertDescription className="text-[11px] leading-snug text-muted-foreground">
                  Сенсорный ввод: узлы на карте отображаются крупнее. Для точной правки приблизьте карту или
                  используйте мышь на ПК.
                </AlertDescription>
              </Alert>
            </div>
          )}
          {isCircleEdit && (
            <div className="border-b border-border px-3 py-2">
              <Alert
                variant="default"
                className="border-amber-500/40 bg-amber-500/10 py-2 dark:border-amber-500/30 dark:bg-amber-950/40"
              >
                <AlertDescription className="text-[11px] leading-snug text-amber-950 dark:text-amber-100">
                  Круг на карте редактируется как полигон из сегментов. Для строго заданного радиуса создайте круг
                  заново или уточните параметры в документации объекта.
                </AlertDescription>
              </Alert>
            </div>
          )}
          {isMultiComplex && (
            <div className="border-b border-border px-3 py-2">
              <Alert variant="default" className="py-2">
                <AlertDescription className="text-[11px] leading-snug">
                  Составной тип ({geometryTypeLabel}): поведение узлов может отличаться от простого полигона.
                  Проверьте результат после сохранения.
                </AlertDescription>
              </Alert>
            </div>
          )}
          <div className="space-y-3 p-3">
            <div>
              <Label className="mb-1 block text-sm text-muted-foreground">Название геометрии</Label>
              <Input
                className="border-border bg-background text-foreground placeholder:text-muted-foreground"
                placeholder="Введите название"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div>
              <Label className="mb-1 block text-sm text-muted-foreground">Описание</Label>
              <Textarea
                placeholder="Введите описание"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-20 resize-none border-border bg-background text-foreground placeholder:text-muted-foreground"
              />
            </div>

            <div>
              <Label className="mb-1 block text-sm text-muted-foreground">Стиль</Label>
              <div className="flex space-x-3">
                <div className="flex items-center">
                  <span className="mr-1 text-xs text-muted-foreground">Цвет:</span>
                  <Input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="h-8 w-8 cursor-pointer rounded border border-border bg-background p-1"
                  />
                </div>
                <div className="flex flex-1 items-center">
                  <span className="mr-1 text-xs text-muted-foreground">Прозрачность:</span>
                  <Input
                    type="range"
                    min="0"
                    max="100"
                    value={opacity}
                    onChange={(e) => setOpacity(parseInt(e.target.value, 10))}
                    className="h-2 w-full cursor-pointer accent-primary"
                  />
                </div>
              </div>
            </div>
          </div>
        </MapPanelChrome>
      </div>

      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отменить изменения?</AlertDialogTitle>
            <AlertDialogDescription>
              Несохранённые правки контура и полей будут потеряны.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Вернуться</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowDiscardDialog(false);
                onCancel();
              }}
            >
              Выйти без сохранения
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
